import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { OverviewScreen } from './screens/OverviewScreen';
import { DetailScreen } from './screens/DetailScreen';
import { SearchScreen } from './screens/SearchScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { ShareScreen } from './screens/ShareScreen';
import { RedeemScreen } from './screens/RedeemScreen';
import { AuthScreen } from './screens/AuthScreen';
import { DeleteListDialog, NewListDialog, RenameListDialog } from './screens/ListDialogs';
import { EmptyState, ErrorBanner, ItemsSkeleton } from './components';
import { useSurface } from './hooks/useSurface';
import { useTelegramContext } from './hooks/useTelegramContext';
import { useLists } from './api/queries';

function useRouteListId(): number | null {
  const { listId } = useParams();
  const parsed = Number(listId);
  return listId && Number.isFinite(parsed) ? parsed : null;
}

/**
 * On desktop the overview route *is* the detail view — the sidebar in
 * `DesktopShell` supplies the list of lists, so rendering a second list here
 * would duplicate it. On mobile it is the two-section overview.
 */
function OverviewRoute() {
  const { isDesktop } = useSurface();
  const navigate = useNavigate();
  const { data: lists, isPending, isError, error, refetch } = useLists();

  if (!isDesktop) return <OverviewScreen />;
  if (isError) {
    return (
      <>
        <ErrorBanner message={error.message} onRetry={() => void refetch()} />
        <div className="screen-content" />
      </>
    );
  }
  if (isPending) {
    return (
      <div className="screen-content content">
        <ItemsSkeleton />
      </div>
    );
  }

  const first = lists?.[0];
  if (!first) {
    return (
      <div className="screen-content content">
        <EmptyState
          glyph="☰"
          title="No lists yet"
          body="You don't have any lists yet. Create one to get started."
          action={{ label: 'Create a list', onClick: () => navigate('/new') }}
        />
      </div>
    );
  }
  return <DetailScreen listId={first.id} />;
}

function DetailRoute() {
  const listId = useRouteListId();
  if (listId === null) return <Navigate to="/" replace />;
  return <DetailScreen listId={listId} />;
}

function ShareRoute() {
  const listId = useRouteListId();
  if (listId === null) return <Navigate to="/" replace />;
  return <ShareScreen listId={listId} />;
}

/** Dialog routes render the screen underneath, so back targets work as ported. */
function RenameRoute() {
  const listId = useRouteListId();
  if (listId === null) return <Navigate to="/" replace />;
  return (
    <>
      <DetailScreen listId={listId} />
      <RenameListDialog listId={listId} />
    </>
  );
}

function DeleteRoute() {
  const listId = useRouteListId();
  if (listId === null) return <Navigate to="/" replace />;
  return (
    <>
      <DetailScreen listId={listId} />
      <DeleteListDialog listId={listId} />
    </>
  );
}

function NewListRoute() {
  return (
    <>
      <OverviewRoute />
      <NewListDialog />
    </>
  );
}

/** Anything but `/auth` and `/redeem/:token` needs a resolved identity. */
function RequireAuth({ children }: { children: ReactNode }) {
  const ctx = useTelegramContext();
  const { pathname, search } = useLocation();
  if (ctx.mode === 'unauthenticated') {
    return <Navigate to={`/auth?next=${encodeURIComponent(pathname + search)}`} replace />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth" element={<AuthScreen />} />
      <Route path="/redeem/:token" element={<RedeemScreen />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<OverviewRoute />} />
        <Route path="/new" element={<NewListRoute />} />
        <Route path="/search" element={<SearchScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/lists/:listId" element={<DetailRoute />} />
        <Route path="/lists/:listId/rename" element={<RenameRoute />} />
        <Route path="/lists/:listId/delete" element={<DeleteRoute />} />
        <Route path="/lists/:listId/share" element={<ShareRoute />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
